import type { AgentTransport, AgentEvent, ConnectionStatus, HistoryMessage, ProviderInfo, ModelInfo, AdapterType } from "@lude/agents-im";
import { useWsStore } from "@/stores/ws";
import { sessionService } from "@/services/session";
import { providerService } from "@/services/provider";

export class WebTransport implements AgentTransport {
  get status(): ConnectionStatus {
    return useWsStore.getState().connected ? "connected" : "disconnected";
  }

  onStatusChange(cb: (status: ConnectionStatus) => void): () => void {
    return useWsStore.subscribe((state) => {
      cb(state.connected ? "connected" : "disconnected");
    });
  }

  sendMessage(agentId: string, sessionId: string, content: string, attachments?: string[]): string {
    const msgId = crypto.randomUUID();
    const ws = useWsStore.getState().ws;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        msg_id: msgId,
        payload: {
          type: "Command",
          data: {
            agent_id: agentId,
            session_id: sessionId,
            command: { type: "SendMessage", data: { content, attachments: attachments || [] } },
          },
        },
      }));
    }
    return msgId;
  }

  sendToolApproval(agentId: string, sessionId: string, toolUseId: string, approved: boolean): void {
    useWsStore.getState().send({
      type: "Command",
      data: {
        agent_id: agentId,
        session_id: sessionId,
        command: { type: "ToolApproval", data: { tool_use_id: toolUseId, approved } },
      },
    });
  }

  interrupt(agentId: string, sessionId: string): void {
    useWsStore.getState().send({
      type: "Command",
      data: {
        agent_id: agentId,
        session_id: sessionId,
        command: { type: "Interrupt" },
      },
    });
  }

  subscribeEvents(sessionId: string, cb: (event: AgentEvent) => void): () => void {
    return useWsStore.getState().subscribeSession(sessionId, (data: any) => {
      if (data.event) {
        const eventKey = typeof data.event === "string" ? data.event : Object.keys(data.event)[0];
        console.log(`[Transport] event for ${sessionId.slice(0,8)}: ${eventKey}`, data.event);
        cb(data.event);
      }
    });
  }

  queryMessages(agentId: string, sessionId: string, opts?: { before?: string; limit?: number }): void {
    sessionService.messages(sessionId, opts?.before, opts?.limit).then((result) => {
      const listeners = (this as any)._historyListeners?.get(sessionId);
      if (listeners) {
        const messages: HistoryMessage[] = result.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          created_at: m.created_at,
        }));
        listeners(({ messages, has_more: result.has_more }));
      }
    });
  }

  private _historyListeners = new Map<string, (data: { messages: HistoryMessage[]; has_more: boolean }) => void>();

  subscribeHistory(sessionId: string, cb: (data: { messages: HistoryMessage[]; has_more: boolean }) => void): () => void {
    this._historyListeners.set(sessionId, cb);
    return () => { this._historyListeners.delete(sessionId); };
  }

  onMessageAck(cb: (ack: { msg_id: string; status: "received" | "delivered" }) => void): () => void {
    return useWsStore.getState().subscribeAll((data: any) => {
      if (data._type === "message_ack" && data.msg_id) {
        cb({ msg_id: data.msg_id, status: data.status });
      }
    });
  }

  async listProviders(): Promise<ProviderInfo[]> {
    const models = await providerService.myModels();
    return models.map((m) => ({
      id: m.provider_id,
      name: m.provider_name,
      provider_type: (m.provider_type === "ApiProxy" ? "ApiProxy" : "OAuth") as "OAuth" | "ApiProxy",
      adapter_type: m.adapter_type as AdapterType,
      default_model: m.models[0] || null,
    }));
  }

  async listModels(providerId: string): Promise<ModelInfo[]> {
    const models = await providerService.myModels();
    const provider = models.find((m) => m.provider_id === providerId);
    if (!provider) return [];
    return provider.models.filter((m) => m !== "*").map((m) => ({
      id: m, display_name: m, max_input_tokens: null, max_output_tokens: null, context_window: null,
    }));
  }

  async switchSessionModel(sessionId: string, adapterType: AdapterType, providerId: string, model: string): Promise<void> {
    useWsStore.getState().send({
      type: "SwitchSessionModel",
      data: { session_id: sessionId, adapter_type: adapterType, provider_id: providerId, model },
    });
  }
}

export const webTransport = new WebTransport();
